import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import FormDots from '../common/FormDots';
import { Button, Col, Input, InputNumber, Row } from 'antd';
import { Form } from '@ant-design/compatible';
import LaborTimeSelect, { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { addItem, getFixedFloatSum, removeItem } from '../../utils/InvoiceUtils';

require('./BuyerInvoiceForm.less');

const LaborFormFields: FC<any> = ({
	formData,
	form,
	currency,
	recalculateTotals,
	initialLaborUuid,
	state,
	setState,
}): React.ReactElement => {
	const [, setLaborUuid] = useState(initialLaborUuid);

	const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

	const laborKeys = getFieldValue('laborKeys') || [];

	const handleLaborChange = useCallback(
		(val, k, keyName) => {
			const laborHourlyRate =
				keyName === 'hourlyRate' ? val : getFieldValue(`labor-${k}-hourlyRate`);
			const laborTime = keyName === 'time' ? val : getFieldValue(`labor-${k}-time`);
			const laborNumberOfTech =
				keyName === 'numberOfTech' ? val : getFieldValue(`labor-${k}-numberOfTech`);
			const laborKeys = getFieldValue(`laborKeys`);
			let amount = 0;
			if (laborHourlyRate && laborTime && laborNumberOfTech) {
				amount =
					((laborHourlyRate * laborTime) / (state.timeIn === TIME_IN.MINUTES ? 60 : 1)) *
					laborNumberOfTech;
				setFieldsValue({ [`labor-${k}-amount`]: amount.toString() });
			} else {
				setFieldsValue({ [`labor-${k}-amount`]: undefined });
			}

			const laborTotalBeforeTax = laborKeys.reduce((acc, k2) => {
				const price = k2 === k ? amount : getFieldValue(`labor-${k2}-amount`) || '0';
				return getFixedFloatSum(acc, price);
			}, 0);
			setFieldsValue({ laborTotalBeforeTax: laborTotalBeforeTax });
			recalculateTotals();
		},
		[getFieldValue, recalculateTotals, setFieldsValue, state.timeIn]
	);

	const onTimeInChange = useCallback(
		(timeIn) => {
			setState({ timeIn });
		},
		[setState]
	);

	useEffect(() => {
		laborKeys.map((key) => handleLaborChange(getFieldValue(`labor-${key}-time`), key, 'time'));
		//This is to trigger labor chanage on timeIn changes. No other dependency should be added
	}, [getFieldValue, handleLaborChange, laborKeys, state.timeIn]);

	const laborFormItems = useMemo(
		() =>
			laborKeys.map((k, laborIndex) => {
				return (
					<Row key={`labor-item-${k}`}>
						<Col span={1}>
							<div
								style={
									laborIndex === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
								}
							>
								{laborIndex + 1}
							</div>
						</Col>

						<Col span={5}>
							<Form.Item label={laborIndex === 0 ? 'Role Type' : ''}>
								{getFieldDecorator(`labor-${k}-roleType`, {
									validateTrigger: ['onBlur'],
									initialValue: nullSafeGetOrElse(`labor-${k}-roleType`, formData, 'Technician'),
									rules: [
										{
											required: true,
											whitespace: true,
											message: 'Please add role type.',
										},
									],
								})(<Input />)}
							</Form.Item>
						</Col>

						<Col span={3} offset={1}>
							<Form.Item label={laborIndex === 0 ? 'Number of Techs' : ''}>
								{getFieldDecorator(`labor-${k}-numberOfTech`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`labor-${k}-numberOfTech`, formData) ||
										nullSafeGet(`laborItemInitialValues.labor-${k}-numberOfTech`, state),
									rules: [
										{
											required: true,
											message: 'Please add number of techs.',
										},
									],
								})(
									<InputNumber
										onChange={(e) => handleLaborChange(e, k, 'numberOfTech')}
										step={1}
										style={{ width: '100%' }}
									/>
								)}
							</Form.Item>
						</Col>

						<Col span={3} offset={1}>
							<Form.Item
								label={
									laborIndex === 0 ? (
										<LaborTimeSelect value={state.timeIn} onChange={onTimeInChange} />
									) : (
										''
									)
								}
							>
								{getFieldDecorator(`labor-${k}-time`, {
									initialValue:
										nullSafeGet(`labor-${k}-time`, formData) ||
										nullSafeGet(`laborItemInitialValues.labor-${k}-time`, state),
									validateTrigger: ['onBlur'],
									rules: [
										{
											required: true,
											message: 'Please add quantity.',
										},
									],
								})(
									<InputNumber
										step={1}
										onChange={(e) => handleLaborChange(e, k, 'time')}
										style={{ width: '100%' }}
									/>
								)}
							</Form.Item>
						</Col>

						<Col span={3} offset={1}>
							<Form.Item label={laborIndex === 0 ? 'Hourly Rate' : ''}>
								{getFieldDecorator(`labor-${k}-hourlyRate`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`labor-${k}-hourlyRate`, formData) ||
										nullSafeGet(`laborItemInitialValues.labor-${k}-hourlyRate`, state),
									rules: [
										{
											whitespace: true,
											required: true,
											message: 'Please add rate.',
										},
									],
								})(
									<InputNumber
										step={0.01}
										onChange={(e) => handleLaborChange(e, k, 'hourlyRate')}
										style={{ width: '100%' }}
										addonAfter={currency.id}
									/>
								)}
							</Form.Item>
						</Col>
						<Col
							span={1}
							style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
						>
							<div
								style={
									laborIndex === 0
										? {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												marginTop: 30,
										  }
										: {
												height: 40,
												display: 'flex',
												alignItems: 'center',
										  }
								}
							>
								{laborKeys.length > 0 ? (
									<MinusCircleOutlined
										className="dynamic-delete-button"
										onClick={() => removeItem(form, recalculateTotals)('laborKeys', 'labor', k)}
									/>
								) : null}
							</div>
						</Col>
						<Col offset={3} span={2}>
							<div
								style={
									laborIndex === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
										  }
								}
							>
								{form.getFieldDecorator(`labor-${k}-amount`, {
									initialValue:
										formData[`labor-${k}-amount`] ||
										nullSafeGet(`laborItemInitialValues.labor-${k}-amount`, state),
								})}
								{currency.format(getFieldValue(`labor-${k}-amount`))}
							</div>
						</Col>
					</Row>
				);
			}),
		[
			laborKeys,
			getFieldDecorator,
			formData,
			state,
			onTimeInChange,
			currency,
			form,
			getFieldValue,
			handleLaborChange,
			recalculateTotals,
		]
	);

	const onAddItem = useCallback(() => {
		setLaborUuid((uuid) =>
			addItem(form, state, setState)('laborKeys', uuid, 'laborTotalBeforeTax')
		);
	}, [form, setState, state]);

	return (
		<div style={{ marginBottom: 32 }}>
			<div className="buyerInvoiceForm__sectionHead">
				<div className="buyerInvoiceForm__sectionHeadPrimary">Labor</div>
				<FormDots />
				{nullSafeGetOrElse('length', laborKeys, 0) > 0 ? (
					getFieldDecorator(`laborTotalBeforeTax`, {
						validateTrigger: ['onBlur'],
						initialValue: `${nullSafeGetOrElse(`laborTotalBeforeTax`, formData, 0)}`,
					})(
						<div className="buyerInvoiceForm__sectionHeadSecondary">
							{currency.format(getFieldValue(`laborTotalBeforeTax`))}
						</div>
					)
				) : (
					<div className="buyerInvoiceForm__sectionHeadSecondary">
						{getFieldDecorator(`laborTotalBeforeTax`, {
							validateTrigger: ['onBlur'],
							initialValue: `${nullSafeGetOrElse(`laborTotalBeforeTax`, formData, 0)}`,
						})(
							<InputNumber
								step={1}
								onChange={recalculateTotals}
								style={{ width: 240, textAlign: 'right' }}
								addonAfter={currency.id}
							/>
						)}
					</div>
				)}
			</div>
			{laborFormItems}
			<Row>
				<Col span={17} offset={1}>
					<Button type="dashed" onClick={onAddItem} style={{ height: 32 }}>
						<PlusOutlined /> Add labor
					</Button>
				</Col>
			</Row>
		</div>
	);
};

export default LaborFormFields;
